// TemplateDiffPanel — renders the diff object returned by apply-template?preview=true.
// Used in TemplatesPage "Apply to Tenant" dialog and FieldsTab bulk actions.

import type { JSX } from "react";
import type { TemplateDiff } from "../lib/adminApiClient.js";

function DiffSection({ title, items, variant }: {
  title: string; items: string[]; variant: "add" | "remove" | "change";
}): JSX.Element | null {
  if (items.length === 0) return null;
  const cls = variant === "add"
    ? "bg-green-50 border-green-200 text-green-800"
    : variant === "remove"
    ? "bg-red-50 border-red-200 text-red-800"
    : "bg-amber-50 border-amber-200 text-amber-800";
  return (
    <div className={`rounded-lg border p-3 ${cls}`}>
      <p className="text-xs font-semibold uppercase tracking-wide mb-2">{title}</p>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item} className="text-xs font-mono">{item}</li>
        ))}
      </ul>
    </div>
  );
}

interface Props { diff: TemplateDiff; }

export function TemplateDiffPanel({ diff }: Props): JSX.Element {
  const hasChanges =
    diff.modulesActivating.length + diff.modulesDeactivating.length +
    diff.fieldsActivating.length + diff.fieldsDeactivating.length +
    diff.fieldsBecomingRequired.length + diff.fieldsBecomingOptional.length > 0;

  if (!hasChanges) {
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-center">
        <p className="text-sm text-slate-500">
          No changes — this tenant already matches this template.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <DiffSection title="Modules to activate"    items={diff.modulesActivating}      variant="add" />
      <DiffSection title="Modules to deactivate"  items={diff.modulesDeactivating}    variant="remove" />
      <DiffSection title="Fields to activate"     items={diff.fieldsActivating}       variant="add" />
      <DiffSection title="Fields to deactivate"   items={diff.fieldsDeactivating}     variant="remove" />
      <DiffSection title="Fields becoming required"  items={diff.fieldsBecomingRequired}  variant="change" />
      <DiffSection title="Fields becoming optional"  items={diff.fieldsBecomingOptional}  variant="change" />
    </div>
  );
}
